import { ref } from 'vue';
import { defineStore } from 'pinia';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getRemoteConfig, fetchAndActivate } from 'firebase/remote-config';

import { env } from '@shared/config';
import { Sentry } from '@/shared/sentry';

import { firebaseLib } from './lib';
import { firebaseConfig } from './config';
import type { AppRemoteConfig } from './types';

const setupRemoteConfig = async (app: FirebaseApp) => {
  const remoteConfig = getRemoteConfig(app);
  /**
   * In production we should set bigger refetch interval,
   * but it is not so important because we fetch the config only once
   * when the app and the component which uses remote config are loaded
   * So we better set it to only 1 min for all changes to be fetch in the next app init
   */
  remoteConfig.settings.minimumFetchIntervalMillis =
    env.MODE === 'production' ? 60000 : 3000;
  remoteConfig.defaultConfig = firebaseConfig.defaultRemoteConfig;

  /**
   * @description As for now we don't have any UI which would create CLS
   * when the config is loaded, so we use fetchAndActivate strategy
   *
   * @see https://firebase.google.com/docs/remote-config/loading
   */
  await fetchAndActivate(remoteConfig).catch(error =>
    Sentry.captureExWithCause('ERR_FAILED_TO_FETCH_OR_ACTIVATE_REMOTE_CONFIG', {
      cause: error,
    }),
  );

  return remoteConfig;
};

/**
 * @description Store to init and use firebase and remote config in apps
 *
 * We must use singleton to send the fetch config request only once per app init
 *
 * useFirebase should be called only on a client side, since remote config uses BrowserAPI
 *
 * @see https://wiki.yandex.ru/tours/frontendv2/remote-config/
 */
export const useFirebase = defineStore('firebase', () => {
  const app = initializeApp({
    apiKey: env.FIREBASE_API_KEY,
    projectId: env.FIREBASE_PROJECT_ID,
    appId: env.FIREBASE_APP_ID,
  });

  const remoteConfig = ref<AppRemoteConfig>(
    firebaseConfig.defaultAppRemoteConfig,
  );
  const isLoading = ref(false);

  const init = () => {
    isLoading.value = true;
    setupRemoteConfig(app)
      .then(config => {
        remoteConfig.value = firebaseLib.parseRemoteConfig(config);
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  init();

  return {
    remoteConfig,
    isLoading,
  };
});
